import * as Y from 'yjs'
import { IndexeddbPersistence } from 'y-indexeddb'
import { WebsocketProvider } from 'y-websocket'
import { WEBSOCKET_URL, getWsAuthParams } from '../../api/config'
import { buildIDBKey } from '../idb/idbKeyManager'
import { idbManifest } from '../idb/idbManifest'
import { deleteIDBDatabase } from '../idb/idbUtils'
import { shutdownCoordinator } from '../idb/shutdownCoordinator'
import { wsTokenRefresher } from '../wsTokenRefresher'

/** Server is durability boundary; client REST CRDT sync disabled */
const SERVER_IS_DURABILITY_BOUNDARY = true

interface SyncProviders {
  ydoc: Y.Doc
  indexeddb: IndexeddbPersistence
  websocket: WebsocketProvider
  cleanup: () => void
}

/**
 * Debug state for a single document (dev-only, non-invasive)
 */
interface DebugState {
  indexeddbSynced: boolean
  wsConnected: boolean
  wsSynced: boolean
  lastSyncAt: number | null
  lastSyncError: string | null
  pendingUpdateCount: number
  loadSource: 'indexeddb' | 'empty' | 'unknown'
}


class LocalFirstSyncService {
  private docs = new Map<string, SyncProviders>()
  private pendingUpdates = new Map<string, Uint8Array[]>()
  private syncTimers = new Map<string, NodeJS.Timeout>()
  private isOnline: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true
  // Debug state tracking (dev-only, minimal overhead)
  private debugState = new Map<string, DebugState>()
  
  getDoc(entityId: string, entityType: string, workspaceId: string): SyncProviders {
    const cacheKey = `${workspaceId}_${entityType}_${entityId}`
    
    if (this.docs.has(cacheKey)) {
      return this.docs.get(cacheKey)!
    }
    
    const ydoc = new Y.Doc({ guid: entityId })
    
    // Initialize debug state
    this.debugState.set(cacheKey, {
      indexeddbSynced: false,
      wsConnected: false,
      wsSynced: false,
      lastSyncAt: null,
      lastSyncError: null,
      pendingUpdateCount: 0,
      loadSource: 'unknown'
    })
    
    // Layer 1: IndexedDB persistence (crash recovery)
    const indexedDBKey = buildIDBKey('entity-sync', entityId, { entityType, workspaceId })
    const indexeddb = new IndexeddbPersistence(indexedDBKey, ydoc)
    idbManifest.register(indexedDBKey, 'entity-sync', entityId)
    
    console.log(`[Sync] 📦 IndexedDB provider created for ${entityId.slice(-8)} (loading...)`)
    
    // Layer 2: WebSocket sync (real-time + server persistence)
    // 🔥 CRITICAL: Create DISCONNECTED, connect AFTER IndexedDB loads
    const wsUrl = this.getWebSocketUrl()
    // Use correct prefix based on entity type
    const roomPrefix = entityType === 'Mail.Draft' ? 'draft' : 
                       entityType === 'Doc' ? 'doc' : 
                       entityType === 'Note' ? 'note' :
                       entityType === 'Page' ? 'page' :
                       entityType === 'Workflow' ? 'workflow' : 'doc'
    const roomName = `${roomPrefix}:${entityId}`
    
    const websocket = new WebsocketProvider(wsUrl, roomName, ydoc, {
      connect: false,  // ← CRITICAL: Don't connect yet!
      params: getWsAuthParams(),
      resyncInterval: 30000,
    })
    wsTokenRefresher.register(websocket)
    
    console.log(`[Sync] 🔌 WebSocket provider created DISCONNECTED for ${entityId.slice(-8)}`)
    console.log(`[Sync] ⏳ Waiting for IndexedDB to load before connecting WebSocket...`)
    
    // 🔥 CRITICAL: Connect WebSocket ONLY AFTER IndexedDB loads
    // This prevents broadcasting empty state before local content loads
    indexeddb.once('synced', () => {
      console.log(`[Sync] ✅ IndexedDB loaded for ${entityId.slice(-8)}`)
      idbManifest.touchLastAccessed(indexedDBKey)
      console.log(`[Sync] 🔌 IndexedDB has content, safe to connect WebSocket now`)
      
      // Update debug state
      const debug = this.debugState.get(cacheKey)
      if (debug) {
        debug.indexeddbSynced = true
        debug.loadSource = 'indexeddb'
      }
      
      // Now connect WebSocket (local content already in Y.Doc)
      websocket.connect()
      console.log(`[Sync] ✅ WebSocket connected for ${entityId.slice(-8)} (local content safe)`)
    })
    
    websocket.on('status', ({ status }: { status: string }) => {
      console.log(`[Sync] WebSocket ${status} for ${entityId.slice(-8)}`)
      
      // Update debug state
      const debug = this.debugState.get(cacheKey)
      if (debug) {
        debug.wsConnected = status === 'connected'
      }
      
      // Track online/offline state
      if (status === 'connected') {
        this.isOnline = true
        if (!SERVER_IS_DURABILITY_BOUNDARY) {
          this.syncToBackend(entityId, entityType, workspaceId, ydoc)
        } else {
          console.log(`[Sync] Server is durability boundary - no client save needed on reconnect`)
        }
      }
    })
    
    websocket.on('sync', (synced: boolean) => {
      if (synced) {
        console.log(`[Sync] ✅ WebSocket synced ${entityId.slice(-8)}`)
        
        // Update debug state
        const debug = this.debugState.get(cacheKey)
        if (debug) {
          debug.wsSynced = synced
        }
      }
    })
    
    // Setup user awareness for multiplayer presence
    this.setupUserAwareness(websocket, entityId)
    
    // Layer 4: Backend API sync - disabled when server is durability boundary
    if (!SERVER_IS_DURABILITY_BOUNDARY) {
      this.setupBackendSync(entityId, entityType, workspaceId, ydoc)
    } else {
      console.log(`[Sync] Backend sync disabled - server is durability boundary`)
    }
    
    const cleanup = () => {
      console.log(`[Sync] 🧹 CLEANUP START for ${entityId.slice(-8)}`)
      
      // 🔥 CRITICAL: Clear awareness state BEFORE destroying provider
      // This tells other clients we're leaving
      if (websocket?.awareness) {
        const localState = websocket.awareness.getLocalState()
        console.log(`[Sync] 🧹 Clearing awareness state:`, JSON.stringify({
          entityId: entityId.slice(-8),
          localState,
          currentStatesCount: websocket.awareness.getStates().size
        }, null, 2))
        
        websocket.awareness.setLocalState(null)
        console.log(`[Sync] ✅ Awareness cleared, now ${websocket.awareness.getStates().size} states remain`)
      }
      
      // Clear any pending sync timer
      const timer = this.syncTimers.get(entityId)
      if (timer) {
        clearTimeout(timer)
        this.syncTimers.delete(entityId)
      }
      
      // Cleanup awareness heartbeat
      const awarenessCleanup = (websocket as any).__awarenessCleanup
      if (awarenessCleanup) {
        console.log(`[Sync] 🧹 Running awareness cleanup callback`)
        awarenessCleanup()
        delete (websocket as any).__awarenessCleanup
      }
      
      // Cleanup providers
      console.log(`[Sync] 🧹 Destroying providers...`)
      wsTokenRefresher.unregister(websocket)
      websocket.destroy()
      indexeddb.destroy()
      ydoc.destroy()

      // Remove from cache
      this.docs.delete(cacheKey)
      this.pendingUpdates.delete(entityId)
      
      console.log(`[Sync] ✅ CLEANUP COMPLETE for ${entityId.slice(-8)}`)
    }
    
    const providers = { 
      ydoc, 
      indexeddb, 
      websocket, 
      cleanup 
    }
    this.docs.set(cacheKey, providers)
    
    console.log(`[Sync] ✅ All providers initialized for ${entityId.slice(-8)}`, {
      indexedDB: true,
      websocket: true,
      backend: true
    })
    
    return providers
  }
  
  async waitForReady(
    entityId: string,
    entityType: string,
    workspaceId: string
  ): Promise<SyncProviders> {
    const providers = this.getDoc(entityId, entityType, workspaceId)
    
    return new Promise((resolve) => {
      if (providers.indexeddb.synced) {
        resolve(providers)
      } else {
        providers.indexeddb.once('synced', () => resolve(providers))
      }
    })
  }
  
  cleanup(entityId: string, entityType: string, workspaceId: string) {
    const cacheKey = `${workspaceId}_${entityType}_${entityId}`
    const providers = this.docs.get(cacheKey)
    if (providers) {
      providers.cleanup()
    }
    // Clean up debug state
    this.debugState.delete(cacheKey)
  }

  /**
   * Deletes all y-indexeddb databases for an entity after it has been
   * permanently deleted. Uses the IDB manifest for cross-browser enumeration
   * (no dependency on Chrome-only indexedDB.databases()).
   *
   * Fire-and-forget: never throws, never blocks the caller.
   */
  async deleteEntityDoc(entityId: string): Promise<void> {
    const deleteDb = deleteIDBDatabase

    // Case A: entity doc is currently open in this tab — clean up live providers.
    // Collect matching keys first to avoid mutating the map during iteration
    // (cleanup() calls this.docs.delete internally).
    const openCacheKeys = Array.from(this.docs.keys()).filter(k => k.endsWith(`_${entityId}`))
    for (const cacheKey of openCacheKeys) {
      const providers = this.docs.get(cacheKey)
      if (providers) {
        providers.cleanup()
        this.debugState.delete(cacheKey)
      }
    }

    // Case B: use manifest to find ALL IDB databases for this entity (cross-browser)
    try {
      const entries = await idbManifest.getEntriesForEntity(entityId)
      for (const entry of entries) {
        await deleteDb(entry.key)
        await idbManifest.unregister(entry.key)
        console.log(`[Sync] 🗑️ Deleted entity IDB: ${entry.key}`)
      }
    } catch {
      // Non-fatal
    }

    // Legacy fallback: also try indexedDB.databases() for old-format keys
    // not yet tracked in the manifest (migration path)
    if ('databases' in indexedDB) {
      try {
        const allDbs = await (indexedDB as any).databases() as { name: string }[]
        for (const db of allDbs) {
          if (db.name?.startsWith('river_') && db.name.endsWith(`_${entityId}`)) {
            await deleteDb(db.name)
            console.log(`[Sync] 🗑️ Deleted legacy entity IDB: ${db.name}`)
          }
        }
      } catch {
        // indexedDB.databases() can fail in some contexts; silently skip
      }
    }
  }
  
  /**
   * Get debug state for a document (dev-only, for overlay UI)
   */
  getDebugState(entityId: string, entityType: string, workspaceId: string): DebugState | null {
    const cacheKey = `${workspaceId}_${entityType}_${entityId}`
    return this.debugState.get(cacheKey) || null
  }
  
  /**
   * Setup background sync to backend API (entities.skeleton.base64)
   * Debounced, queued when offline, automatic retry
   */
  private setupBackendSync(
    entityId: string, 
    entityType: string,
    workspaceId: string,
    ydoc: Y.Doc
  ) {
    ydoc.on('update', (update: Uint8Array, origin: any) => {
      // Skip if update came from backend (prevent loop)
      if (origin === 'backend-sync') return
      
      // Skip if update came from WebSocket (server already persisting)
      if (origin?.constructor?.name === 'WebsocketProvider') return
      
      // Queue update for backend
      if (!this.pendingUpdates.has(entityId)) {
        this.pendingUpdates.set(entityId, [])
      }
      this.pendingUpdates.get(entityId)!.push(update)
      
      // Schedule debounced sync (2s wait)
      this.scheduleSyncDebounced(entityId, entityType, workspaceId, ydoc)
    })
  }
  
  /**
   * Schedule debounced backend sync (2s after last edit)
   */
  private scheduleSyncDebounced(
    entityId: string,
    entityType: string,
    workspaceId: string,
    ydoc: Y.Doc
  ) {
    // Clear existing timer
    const existingTimer = this.syncTimers.get(entityId)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }
    
    // Set new timer
    const timer = setTimeout(() => {
      this.syncToBackend(entityId, entityType, workspaceId, ydoc)
    }, 2000) // 2 second debounce
    
    this.syncTimers.set(entityId, timer)
  }
  
  /**
   * Sync to backend API - uses EXISTING /api/entities/:id/merge endpoint!
   * No new backend code needed!
   */
  private async syncToBackend(
    entityId: string,
    entityType: string,
    workspaceId: string,
    ydoc: Y.Doc
  ) {
    if (!this.isOnline) {
      console.log(`[Sync] ⚠️ Offline, queuing updates for ${entityId.slice(-8)}`)
      return // Keep updates in queue
    }
    
    const updates = this.pendingUpdates.get(entityId) || []
    if (updates.length === 0) return
    
    try {
      // Encode full Y.Doc state as base64
      const state = Y.encodeStateAsUpdateV2(ydoc)
      const uint8Array = new Uint8Array(state)
      
      // Convert to base64 (same chunking as docsCRDTService for consistency)
      const binaryChunks: string[] = []
      const chunkSize = 8192
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.slice(i, i + chunkSize)
        binaryChunks.push(String.fromCharCode(...chunk))
      }
      const base64 = btoa(binaryChunks.join(''))
      
      // Extract derived text for search
      let derivedText = ''
      if (entityType === 'Mail.Draft') {
        // Try new format (XmlFragment) first, fallback to Y.Text
        const yxml = ydoc.getXmlFragment('body-editor')
        if (yxml.length > 0) {
          derivedText = this.extractTextFromYXml(yxml)
        } else {
          // Fallback to Y.Text for legacy drafts
          try {
            const ytext = ydoc.getText('body')
            derivedText = ytext.toString()
          } catch (e) {
            // Neither exists, empty draft
            derivedText = ''
          }
        }
      } else if (entityType === 'Doc' || entityType === 'Page') {
        // For documents and pages, extract text from ProseMirror structure
        const yxml = ydoc.getXmlFragment('prosemirror')
        derivedText = this.extractTextFromYXml(yxml)
      }
      
      // Import entityService dynamically to avoid circular dependencies
      const { entityService } = await import('../entityService')
      
      await entityService.mergeUpdate(entityId, {
        skeleton: {
          base64,  // Y.js state
          bodyPlain: derivedText,
          content: derivedText,
          metadata: {
            lastSaved: new Date().toISOString(),
            nodeCount: entityType === 'Doc' ? ydoc.getXmlFragment('prosemirror').length : undefined
          }
        }
      })
      
      // Clear queue on success
      this.pendingUpdates.delete(entityId)
      console.log(`[Sync] ✅ Backend synced for ${entityId.slice(-8)} (${updates.length} updates)`)
      
      // Update debug state
      const cacheKey = `${workspaceId}_${entityType}_${entityId}`
      const debug = this.debugState.get(cacheKey)
      if (debug) {
        debug.lastSyncAt = Date.now()
        debug.lastSyncError = null
        debug.pendingUpdateCount = 0
      }
      
    } catch (error) {
      console.error(`[Sync] ❌ Backend sync failed for ${entityId.slice(-8)}:`, error)
      console.log(`[Sync] Updates queued, will retry when online or on next edit`)
      
      // Update debug state
      const cacheKey = `${workspaceId}_${entityType}_${entityId}`
      const debug = this.debugState.get(cacheKey)
      if (debug) {
        debug.lastSyncError = error instanceof Error ? error.message : String(error)
        debug.pendingUpdateCount = updates.length
      }
      
      // Keep updates in queue for retry
    }
  }
  
  /**
   * Extract plain text from Y.XmlFragment (for Doc search)
   */
  private extractTextFromYXml(yxml: Y.XmlFragment): string {
    let text = ''
    
    const traverse = (item: any) => {
      if (item instanceof Y.XmlElement) {
        // Recursively extract from children
        for (let i = 0; i < item.length; i++) {
          traverse(item.get(i))
        }
      } else if (item instanceof Y.XmlText) {
        text += item.toString() + ' '
      }
    }
    
    for (let i = 0; i < yxml.length; i++) {
      traverse(yxml.get(i))
    }
    
    return text.trim()
  }
  
  /**
   * Sync all pending updates (called when back online)
   */
  async syncAllPending() {
    console.log(`[Sync] Syncing ${this.pendingUpdates.size} documents with pending updates...`)
    
    const syncPromises: Promise<void>[] = []
    
    for (const [entityId] of this.pendingUpdates.entries()) {
      // Find the doc info
      for (const [cacheKey, providers] of this.docs.entries()) {
        if (cacheKey.includes(entityId)) {
          // Extract entityType and workspaceId from cacheKey
          const [workspaceId, entityType] = cacheKey.split('_')
          syncPromises.push(
            this.syncToBackend(entityId, entityType, workspaceId, providers.ydoc)
          )
          break
        }
      }
    }
    
    await Promise.allSettled(syncPromises)
    console.log(`[Sync] ✅ Pending sync complete`)
  }
  
  /**
   * Get sync status for UI
   */
  getSyncStatus(entityId: string): { hasPendingUpdates: boolean; isOnline: boolean } {
    return {
      hasPendingUpdates: (this.pendingUpdates.get(entityId)?.length || 0) > 0,
      isOnline: this.isOnline
    }
  }
  
  /**
   * Get users currently viewing/editing a document
   * Returns array of user info for presence display
   * Also returns total client count (for showing "you in 2 tabs")
   */
  getPresence(entityId: string, entityType: string, workspaceId: string): {
    users: Array<{
      id: string
      name: string
      email: string
      color: string
    }>
    totalClients: number
  } {
    const cacheKey = `${workspaceId}_${entityType}_${entityId}`
    const providers = this.docs.get(cacheKey)
    
    if (!providers?.websocket?.awareness) {
      console.log(`[Sync] 🎭 getPresence: No awareness for ${entityId.slice(-8)}`)
      return { users: [], totalClients: 0 }
    }
    
    const states = providers.websocket.awareness.getStates()
    const totalClients = states.size
    const users: Array<{ id: string; name: string; email: string; color: string }> = []
    
    console.log(`[Sync] 🎭 getPresence for ${entityId.slice(-8)}:`, JSON.stringify({
      totalStates: states.size,
      myClientId: providers.websocket.awareness.clientID,
      allStates: Array.from(states.entries()).map(([clientId, state]) => ({
        clientId,
        isMe: clientId === providers.websocket.awareness.clientID,
        hasUser: !!state.user,
        userId: state.user?.id?.slice(-8),
        userName: state.user?.name,
        userEmail: state.user?.email
      }))
    }, null, 2))
    
    const seenUserIds = new Set<string>()
    
    states.forEach((state: any, clientId: number) => {
      if (state.user) {
        // 🔥 DEDUPLICATE: Same user in multiple tabs should only appear once
        if (seenUserIds.has(state.user.id)) {
          console.log(`[Sync] 🎭 Skipping duplicate user:`, JSON.stringify({
            clientId,
            userId: state.user.id?.slice(-8),
            userName: state.user.name,
            reason: 'User already in list (multiple tabs)'
          }))
          return
        }
        
        seenUserIds.add(state.user.id)
        
        console.log(`[Sync] 🎭 Adding user to presence list:`, JSON.stringify({
          clientId,
          userId: state.user.id?.slice(-8),
          userName: state.user.name,
          userEmail: state.user.email
        }))
        
        users.push({
          id: state.user.id,
          name: state.user.name,
          email: state.user.email,
          color: state.user.color
        })
      }
    })
    
    console.log(`[Sync] 🎭 getPresence returning ${users.length} UNIQUE users, ${totalClients} total clients for ${entityId.slice(-8)}`)
    
    return { users, totalClients }
  }
  
  /**
   * Subscribe to presence changes
   * Returns unsubscribe function
   */
  subscribeToPresence(
    entityId: string,
    entityType: string,
    workspaceId: string,
    callback: (users: Array<{ id: string; name: string; email: string; color: string }>) => void
  ): (() => void) | null {
    const cacheKey = `${workspaceId}_${entityType}_${entityId}`
    const providers = this.docs.get(cacheKey)
    
    if (!providers?.websocket?.awareness) {
      return null
    }
    
    const handler = () => {
      const { users } = this.getPresence(entityId, entityType, workspaceId)
      callback(users)
    }
    
    providers.websocket.awareness.on('change', handler)
    
    // Return unsubscribe function
    return () => {
      providers.websocket.awareness?.off('change', handler)
    }
  }
  
  /**
   * Setup user awareness for collaborative presence
   * Extracts user info and sets awareness state
   */
  private async setupUserAwareness(provider: WebsocketProvider, entityId: string) {
    if (!provider?.awareness) {
      console.warn('[Sync] No awareness available on provider')
      return
    }
    
    console.log(`[Sync] 🎭 AWARENESS SETUP START for ${entityId.slice(-8)}`)
    console.log(`[Sync] 🎭 Current awareness states BEFORE setup:`, JSON.stringify({
      totalStates: provider.awareness.getStates().size,
      clientId: provider.awareness.clientID,
      allStates: Array.from(provider.awareness.getStates().entries()).map(([id, state]) => ({
        clientId: id,
        userId: state.user?.id?.slice(-8),
        userName: state.user?.name
      }))
    }, null, 2))
    
    try {
      // Get current user information
      let userId = 'anonymous'
      let userName = 'Anonymous User'
      let userEmail = ''
      
      // Try to get user info from userService
      try {
        const { userService } = await import('../users/userService')
        const userProfile = await userService.getUserProfile()
        
        if (userProfile?.skeleton) {
          userId = userProfile.skeleton.auth0?.sub || 'anonymous'
          userName = userProfile.skeleton.name || 
                    userProfile.skeleton.auth0?.name || 
                    userProfile.skeleton.auth0?.nickname ||
                    userProfile.skeleton.email ||
                    'Anonymous User'
          userEmail = userProfile.skeleton.email || 
                     userProfile.skeleton.auth0?.email || 
                     ''
        }
      } catch (error) {
        console.warn('[Sync] Could not fetch user profile for awareness:', error)
      }
      
      // Fallback: Try userStore
      if (userId === 'anonymous') {
        try {
          const { default: useUserStore } = await import('../../stores/userStore')
          const userState = useUserStore.getState()
          
          if (userState.auth0Id) {
            userId = userState.auth0Id
            userName = userState.profile?.skeleton?.name || 
                      userState.profile?.skeleton?.auth0?.name ||
                      userState.profile?.skeleton?.email ||
                      'User'
            userEmail = userState.profile?.skeleton?.email || 
                       userState.profile?.skeleton?.auth0?.email ||
                       ''
          }
        } catch (error) {
          console.warn('[Sync] Could not access userStore:', error)
        }
      }
      
      // Generate consistent color for user
      const getUserColor = (id: string) => {
        const colors = [
          '#3b82f6', '#ef4444', '#10b981', '#f59e0b',
          '#8b5cf6', '#06b6d4', '#84cc16', '#f97316',
          '#ec4899', '#6366f1', '#14b8a6', '#f472b6'
        ]
        const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
        return colors[hash % colors.length]
      }
      
      // Set awareness state
      const userAwarenessState = {
        user: {
          id: userId,
          name: userName,
          email: userEmail,
          color: getUserColor(userId)
        }
      }
      
      provider.awareness.setLocalState(userAwarenessState)
      
      console.log(`[Sync] ✅ User awareness set for multiplayer:`, JSON.stringify({
        entityId: entityId.slice(-8),
        myClientId: provider.awareness.clientID,
        userId: userId.slice(-8),
        userName,
        userEmail,
        color: getUserColor(userId),
        totalStatesAfter: provider.awareness.getStates().size,
        allStatesAfter: Array.from(provider.awareness.getStates().entries()).map(([id, state]) => ({
          clientId: id,
          isMe: id === provider.awareness.clientID,
          userId: state.user?.id?.slice(-8),
          userName: state.user?.name,
          userEmail: state.user?.email
        }))
      }, null, 2))
      
      // Add awareness change listener for debugging
      const awarenessChangeHandler = ({ added, updated, removed }: { 
        added: number[]
        updated: number[]
        removed: number[] 
      }) => {
        console.log(`[Sync] 🎭 AWARENESS CHANGE for ${entityId.slice(-8)}:`, JSON.stringify({
          added,
          updated,
          removed,
          totalNow: provider.awareness.getStates().size,
          myClientId: provider.awareness.clientID,
          allUsers: Array.from(provider.awareness.getStates().entries()).map(([id, state]) => ({
            clientId: id,
            isMe: id === provider.awareness.clientID,
            userId: state.user?.id?.slice(-8),
            userName: state.user?.name
          }))
        }, null, 2))
      }
      
      provider.awareness.on('change', awarenessChangeHandler)
      
      // Periodic heartbeat to keep awareness alive
      const heartbeat = setInterval(() => {
        if (document.visibilityState === 'visible' && userId !== 'anonymous') {
          const currentLocal = provider.awareness.getLocalState()
          if (!currentLocal?.user) {
            console.log(`[Sync] 💓 Heartbeat: Restoring lost awareness for ${entityId.slice(-8)}`)
            provider.awareness.setLocalState(userAwarenessState)
          }
        }
      }, 10000) // Every 10 seconds
      
      // Store cleanup in provider
      const awarenessCleanup = () => {
        console.log(`[Sync] 🧹 Awareness cleanup callback running for ${entityId.slice(-8)}`)
        provider.awareness.off('change', awarenessChangeHandler)
        clearInterval(heartbeat)
      }
      ;(provider as any).__awarenessCleanup = awarenessCleanup
      
    } catch (error) {
      console.error('[Sync] Error setting up awareness:', error)
    }
  }
  
  private getWebSocketUrl(): string {
    return WEBSOCKET_URL
  }
  
  /**
   * Initialize network monitoring
   */
  private initNetworkMonitoring() {
    if (typeof window === 'undefined') return
    
    window.addEventListener('online', () => {
      console.log('[Sync] 🌐 Back online')
      this.isOnline = true
      if (!SERVER_IS_DURABILITY_BOUNDARY) {
        this.syncAllPending()
      }
    })
    
    window.addEventListener('offline', () => {
      console.log('[Sync] ⚠️ Offline')
      this.isOnline = false
    })
  }
  
  private registerShutdownHandlers() {
    shutdownCoordinator.register({
      name: 'localFirstSyncService:awareness',
      phase: 'awareness',
      handler: () => {
        this.docs.forEach((providers) => {
          if (providers.websocket?.awareness) {
            providers.websocket.awareness.setLocalState(null)
          }
        })
      }
    })

    shutdownCoordinator.register({
      name: 'localFirstSyncService:destroy',
      phase: 'destroy',
      handler: () => {
        for (const [, providers] of this.docs.entries()) {
          try {
            providers.websocket?.destroy()
            providers.indexeddb?.destroy()
            providers.ydoc?.destroy()
          } catch {}
        }
        this.docs.clear()
        this.pendingUpdates.clear()
      }
    })
  }
  
  constructor() {
    this.initNetworkMonitoring()
    this.registerShutdownHandlers()
  }

  /** Returns a snapshot of all active Y.Doc rooms for the debug panel. */
  public getDebugSnapshot(): Array<{
    room: string;
    wsConnected: boolean;
    wsSynced: boolean;
    wsUrl: string;
    idbSynced: boolean;
    docKeys: string[];
  }> {
    const result: ReturnType<typeof this.getDebugSnapshot> = []
    for (const [cacheKey, providers] of this.docs.entries()) {
      const docKeys: string[] = []
      providers.ydoc?.share?.forEach((_val: any, key: string) => docKeys.push(key))
      result.push({
        room: cacheKey,
        wsConnected: providers.websocket?.wsconnected ?? false,
        wsSynced: providers.websocket?.synced ?? false,
        wsUrl: (providers.websocket as any)?.url ?? '',
        idbSynced: providers.indexeddb?.synced ?? false,
        docKeys,
      })
    }
    return result
  }
}

export const localFirstSyncService = new LocalFirstSyncService()
